import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import ImageSelector from "@/components/admin/ImageSelector";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
});

const AdminTeam = () => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    role: { fr: "", en: "" },
    bio: { fr: "", en: "" },
    image_url: "",
    experience_years: 1,
  });

  useEffect(() => {
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    try {
      const response = await axios.get(`${API}/admin/team`, getAuthHeaders());
      setTeam(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/team/${editingItem.id}`, formData, getAuthHeaders());
        toast.success("Membre mis à jour");
      } else {
        await axios.post(`${API}/admin/team`, formData, getAuthHeaders());
        toast.success("Membre ajouté");
      }
      setDialogOpen(false);
      setEditingItem(null);
      fetchTeam();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      role: item.role,
      bio: item.bio,
      image_url: item.image_url,
      experience_years: item.experience_years,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Désactiver ce membre ?")) return;
    try {
      await axios.delete(`${API}/admin/team/${id}`, getAuthHeaders());
      toast.success("Membre désactivé");
      fetchTeam();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleToggleActive = async (item) => {
    try {
      await axios.put(`${API}/admin/team/${item.id}`, { is_active: !item.is_active }, getAuthHeaders());
      toast.success(item.is_active ? "Membre masqué" : "Membre activé");
      fetchTeam();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      name: "",
      role: { fr: "", en: "" },
      bio: { fr: "", en: "" },
      image_url: "",
      experience_years: 1,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="admin-team">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl font-bold">Équipe</h1>
          <p className="text-muted-foreground">Gérez les membres de votre équipe</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-team-btn">
              <Plus className="h-4 w-4" /> Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingItem ? "Modifier le membre" : "Nouveau membre"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Nom complet</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Rôle (FR)</Label>
                  <Input
                    value={formData.role.fr}
                    onChange={(e) => setFormData({ ...formData, role: { ...formData.role, fr: e.target.value } })}
                    required
                  />
                </div>
                <div>
                  <Label>Rôle (EN)</Label>
                  <Input
                    value={formData.role.en}
                    onChange={(e) => setFormData({ ...formData, role: { ...formData.role, en: e.target.value } })}
                  />
                </div>
              </div>
              <div>
                <Label>Bio (FR)</Label>
                <Textarea
                  value={formData.bio.fr}
                  onChange={(e) => setFormData({ ...formData, bio: { ...formData.bio, fr: e.target.value } })}
                  required
                />
              </div>
              <div>
                <Label>Bio (EN)</Label>
                <Textarea
                  value={formData.bio.en}
                  onChange={(e) => setFormData({ ...formData, bio: { ...formData.bio, en: e.target.value } })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Années d'expérience</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.experience_years}
                    onChange={(e) => setFormData({ ...formData, experience_years: parseInt(e.target.value) })}
                    required
                  />
                </div>
              </div>
              <ImageSelector
                label="Photo du membre"
                value={formData.image_url}
                onChange={(url) => setFormData({ ...formData, image_url: url })}
              />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                <Button type="submit">{editingItem ? "Mettre à jour" : "Créer"}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {team.map((member) => (
          <Card key={member.id} className={`overflow-hidden ${!member.is_active ? "opacity-60" : ""}`}>
            <div className="relative h-48">
              <img src={member.image_url} alt="" className="w-full h-full object-cover" />
              {!member.is_active && (
                <Badge className="absolute top-2 right-2" variant="secondary">Inactif</Badge>
              )}
            </div>
            <CardContent className="p-4">
              <h3 className="font-semibold text-lg">{member.name}</h3>
              <p className="text-primary text-sm mb-2">{member.role.fr}</p>
              <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{member.bio.fr}</p>
              <p className="text-xs text-muted-foreground mb-4">{member.experience_years} ans d'expérience</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleEdit(member)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleToggleActive(member)}>
                  {member.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button size="sm" variant="outline" className="text-destructive" onClick={() => handleDelete(member.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminTeam;
